import {useState, Suspense} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Avatar, Detail, Icon, Pill, PillVariant, SubHeading, Text} from '@forecast-it/design-system';
import type {TicketMetaPanel_ticket$key} from './__generated__/TicketMetaPanel_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import type {TicketChannel, TicketPriority, TicketStatus} from '../types/ticket';
import {PriorityBadge} from './PriorityBadge';
import {TicketStatusBadge} from './TicketStatusBadge';
import {MetaChip} from './MetaChip';
import {MetaField} from './MetaField';
import {relativeTime} from '../utils/relativeTime';
import {AssigneePicker} from './AssigneePicker';
import {TicketLabelPicker} from './TicketLabelPicker';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {useSetTicketLabels} from '../hooks/useSetTicketLabels';
import type {UpdateFieldOptions} from '../hooks/useTicketUpdate';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';
import {SLABadge} from './SLABadge';
import {STATUS_ORDER} from '../constants/statuses';
import {PRIORITY_ORDER} from '../constants/priority';
import {CHANNEL_ICONS, CHANNEL_LABELS} from '../constants/channels';
import {selectableChannels} from '../constants/selectableChannels';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import UpdateTicketCustomFieldValueMutation from '../../../../../mutations/ticketing/tickets/updateTicketCustomFieldValueMutation';

const TicketSidebarFragment = graphql`
	fragment TicketMetaPanel_ticket on Ticket {
		id
		status
		priority
		channel
		assigneeId
		assignee {
			...ticketingPerson_person
		}
		requester {
			id
			name
			email
		}
		createdAt
		updatedAt
		closedAt
		labels {
			id
			name
			color
			category {
				name
			}
		}
		parentTicket {
			id
			number
			subject
			status
		}
		links {
			id
			linkType
			targetProject {
				id
				name
			}
		}
		customFields {
			edges {
				node {
					__typename
					id
					key
					type
					displayName
					value
					displayValue
					... on TextCustomFieldValue {
						readOnly
					}
					... on IntegerCustomFieldValue {
						readOnly
					}
					... on DecimalCustomFieldValue {
						readOnly
					}
					... on DateCustomFieldValue {
						readOnly
					}
					... on DropdownCustomFieldValue {
						options {
							id
							displayName
						}
					}
				}
			}
		}
		sla {
			breached
			elapsedSeconds
			thresholdSeconds
			paused
		}
	}
`;

interface Props {
	ticket: TicketMetaPanel_ticket$key;
	saving?: boolean;
	onUpdateField?: (field: string, value: unknown, options?: UpdateFieldOptions) => void;
	onNavigateToRelated?: () => void;
}

const Panel = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	padding: ${p => p.theme.sys.spacing[400]};
`;
// The live state reads across in one line: what it is, how urgent, how long is left.
const StateRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const Divider = styled.div`
	border-top: 1px solid ${p => p.theme.sys.color.border.decorative};
`;
const Fields = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const LabelRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const LabelChip = styled.span<{$color?: string}>`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
	border-radius: 9999px;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	padding: 2px ${p => p.theme.sys.spacing[200]};
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.main};
`;
const LabelDot = styled.span`
	display: inline-block;
	flex: none;
	width: 8px;
	height: 8px;
	border-radius: 9999px;
`;
// Quiet at rest: a caret alone once there are labels, the invitation spelled out when
// there are none.
const AddLabel = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
	border: 1px solid transparent;
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	background: transparent;
	padding: ${p => p.theme.sys.spacing[100]};
	cursor: pointer;
	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
`;
const RemoveLabel = styled.button`
	display: inline-flex;
	align-items: center;
	border: 0;
	background: transparent;
	padding: 0;
	cursor: pointer;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
	&:disabled {
		cursor: default;
	}
`;
// Channel and creation time are provenance: read-only facts about where the ticket
// came from, so they close the panel rather than sitting among the live fields.
const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const Provenance = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const ProjectTrigger = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
	border: 1px solid transparent;
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	background: transparent;
	padding: ${p => p.theme.sys.spacing[100]};
	cursor: pointer;
	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
`;
const CustomFieldRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const CustomFieldInput = styled.input`
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	padding: ${p => p.theme.sys.spacing[100]} ${p => p.theme.sys.spacing[200]};
`;
const CustomFieldSelect = styled.select`
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	padding: ${p => p.theme.sys.spacing[100]} ${p => p.theme.sys.spacing[200]};
	background: ${p => p.theme.sys.color.background.neutral['1']};
`;

type OpenField = 'assignee' | 'labels' | null;

/**
 * The ticket's own fields, beside whichever tab is open.
 *
 * There is no edit mode. The panel is read far more often than it is changed, so each
 * field shows its value and opens its own control when pressed — a mode you enter and
 * leave to change one status is more ceremony than the change is worth.
 *
 * The order is live state first (status, priority, SLA), then who and where (assignee,
 * requester, project), then the ticket's own attributes (labels, custom fields), then
 * provenance. Only the first group changes minute to minute, so it leads.
 */
export function TicketMetaPanel({ticket: ticketRef, saving = false, onUpdateField, onNavigateToRelated}: Props) {
	const [openField, setOpenField] = useState<OpenField>(null);
	const [customFieldEdits, setCustomFieldEdits] = useState<Record<string, string>>({});
	const {users} = useMentionUsers();
	const ticket = useFragment(TicketSidebarFragment, ticketRef);
	const assignee = useFragment<ticketingPerson_person$key>(ticketingPersonFragment, ticket.assignee ?? null);
	const assigneeLabel = ticket.assignee ? personDisplayName(assignee) : ticket.assigneeId ?? 'Unassigned';
	// "Unassigned" is placeholder copy, not a person — an avatar there would render a
	// bogus "U" initial, so the row falls back to a muted profile glyph instead.
	const hasAssignee = Boolean(ticket.assignee ?? ticket.assigneeId);
	const requesterLabel = ticket.requester?.name ?? ticket.requester?.email ?? null;
	const {setLabels, inFlight: labelSaving} = useSetTicketLabels();
	// Every field here is an updateTicket-backed write, so they all gate on the same
	// manage permission the gateway enforces.
	const {canManage} = useTicketPerms();

	// The Related tab is the only place a project link is created or removed, so this row
	// reads that relation instead of owning a second write path. Links arrive oldest-first,
	// so when older data carries more than one project link the same one always wins.
	const projectLink = ticket.links.find(l => l.linkType === 'project') ?? null;
	const projectName = projectLink?.targetProject?.name ?? null;

	// The raw string is kept while editing — an emptied field is '' here, not null, so
	// it stays empty instead of falling back to the server value. It becomes null
	// ("clear") only when sent.
	function handleCustomFieldChange(key: string, value: string) {
		setCustomFieldEdits(prev => ({...prev, [key]: value}));
	}

	// A local edit only bridges the gap until the mutation settles: on success the
	// response has written the value into the store, and on failure the field reverts to
	// the server value. Either way it is dropped, so later store changes show through —
	// unless the user has edited the field again since, in which case the newer edit stays.
	function saveCustomField(key: string, value: string) {
		const clearEdit = () =>
			setCustomFieldEdits(prev => {
				if (prev[key] !== value) return prev;
				const next = {...prev};
				delete next[key];
				return next;
			});
		commitTicketingMutation(
			UpdateTicketCustomFieldValueMutation,
			{
				entityId: ticket.id,
				entityType: 'TICKET',
				key,
				value: value === '' ? null : value,
			},
			clearEdit,
			(error, onFailure) => {
				// The default handler shows the error modal; `true` means it has scheduled a
				// retry instead, which settles through these same callbacks.
				if (!onFailure(error)) clearEdit();
			}
		);
	}

	// Custom fields commit when the field is left rather than on every keystroke: one
	// write per edit, and no request per character.
	function commitCustomField(key: string) {
		const value = customFieldEdits[key];
		if (value === undefined) return;
		saveCustomField(key, value);
	}

	function handleAddLabel(labelId: string) {
		const currentIds = ticket.labels.map(l => l.id);
		setLabels(ticket.id, [...currentIds, labelId]);
	}

	function handleRemoveLabel(labelId: string) {
		const currentIds = ticket.labels.map(l => l.id).filter(id => id !== labelId);
		setLabels(ticket.id, currentIds);
	}

	const channelIcon = CHANNEL_ICONS[ticket.channel as TicketChannel];

	return (
		<Panel data-testid="ticket-meta-panel">
			<SubHeading>Details</SubHeading>

			<StateRow>
				<MetaChip
					label="Change status"
					options={STATUS_ORDER}
					current={ticket.status as TicketStatus}
					disabled={!canManage || saving}
					onSelect={status => onUpdateField?.('status', status)}
					renderValue={status => <TicketStatusBadge status={status} />}
					data-cy="meta-status"
				/>
				<MetaChip
					label="Change priority"
					options={PRIORITY_ORDER}
					current={ticket.priority as TicketPriority}
					disabled={!canManage || saving}
					onSelect={priority => onUpdateField?.('priority', priority)}
					renderValue={priority => <PriorityBadge priority={priority} />}
					data-cy="meta-priority"
				/>
				{/* An SLA is derived, not chosen — there is nothing to open. */}
				<SLABadge ticket={{sla: ticket.sla}} compact />
			</StateRow>

			<Divider />

			<Fields>
				<MetaField
					label="Assignee"
					open={openField === 'assignee'}
					onOpen={() => setOpenField('assignee')}
					readOnly={!canManage}
					data-cy="meta-assignee"
					editor={
						<AssigneePicker
							assigneeId={ticket.assigneeId ?? null}
							users={users}
							disabled={saving}
							defaultOpen
							onChange={(userId, note) => {
								onUpdateField?.('assigneeId', userId, note ? {handoff_note: note} : undefined);
								setOpenField(null);
							}}
						/>
					}
				>
					{hasAssignee ? (
						<>
							<Avatar size="s" name={assigneeLabel} />
							<Text size="2">{assigneeLabel}</Text>
						</>
					) : (
						<>
							<Icon icon="profile" size="s" newColor="disabled" />
							<Text size="2" color="auxiliary">
								Unassigned
							</Text>
						</>
					)}
				</MetaField>

				{/* The requester is who the ticket came from — changing it would change
				    whose ticket it is, and there is no mutation for that. */}
				<MetaField label="Requester" open={false} onOpen={() => {}} readOnly editor={null} data-cy="meta-requester">
					{requesterLabel ? (
						<>
							<Avatar size="s" name={requesterLabel} />
							<Text size="2">{requesterLabel}</Text>
						</>
					) : (
						<Text size="2" color="auxiliary">
							No requester
						</Text>
					)}
				</MetaField>

				<MetaField label="Project" open={false} onOpen={() => {}} readOnly editor={null} data-cy="meta-project">
					{projectLink === null ? (
						<Text size="2" color="auxiliary">
							No project
						</Text>
					) : projectName !== null ? (
						onNavigateToRelated ? (
							<ProjectTrigger
								type="button"
								aria-label={`View project ${projectName} in Related`}
								onClick={onNavigateToRelated}
							>
								<Text size="2">{projectName}</Text>
							</ProjectTrigger>
						) : (
							<Text size="2">{projectName}</Text>
						)
					) : (
						<Text size="2" color="auxiliary">
							Project unavailable
						</Text>
					)}
				</MetaField>

				{ticket.parentTicket && (
					<MetaField label="Parent ticket" open={false} onOpen={() => {}} readOnly editor={null}>
						<Link to={`/tickets/${ticket.parentTicket.id}`}>{`TK-${ticket.parentTicket.number}`}</Link>
					</MetaField>
				)}
			</Fields>

			<LabelRow data-testid="meta-labels">
				{ticket.labels.map(label => (
					<LabelChip key={label.id}>
						<LabelDot aria-hidden="true" style={{backgroundColor: label.color}} />
						{label.name}
						{canManage && (
							<RemoveLabel
								type="button"
								aria-label={`Remove ${label.name}`}
								disabled={labelSaving}
								onClick={() => handleRemoveLabel(label.id)}
							>
								<Icon icon="closeS" size="s" />
							</RemoveLabel>
						)}
					</LabelChip>
				))}
				{/* The picker mounts only once it is opened. It loads the company's label
				    set, and that query has no business running every time a ticket is
				    read. */}
				{canManage &&
					(openField === 'labels' ? (
						<Suspense fallback={<Detail color="medium">Loading…</Detail>}>
							<TicketLabelPicker
								ticketId={ticket.id}
								currentLabelIds={ticket.labels.map(l => l.id)}
								disabled={labelSaving}
								onAdd={labelId => {
									handleAddLabel(labelId);
									setOpenField(null);
								}}
							/>
						</Suspense>
					) : (
						<AddLabel type="button" aria-label="Add labels" onClick={() => setOpenField('labels')}>
							{ticket.labels.length === 0 && (
								<Text size="2" color="auxiliary">
									Add labels
								</Text>
							)}
							<Icon icon="caretDown" size="s" newColor="disabled" />
						</AddLabel>
					))}
				{ticket.labels.length === 0 && !canManage && (
					<Text size="2" color="auxiliary">
						No labels
					</Text>
				)}
			</LabelRow>

			{(ticket.customFields.edges ?? []).map(edge => {
				const node = edge?.node;
				// Skip edges Relay couldn't resolve to a concrete type.
				if (!node || !node.__typename || node.__typename === '%other') return null;
				// Dropdown nodes carry no `readOnly` field → editable; the scalar types
				// (Text/Integer/Decimal/Date) expose `readOnly`.
				const readOnly = 'readOnly' in node ? node.readOnly === true : false;
				const options = 'options' in node ? node.options ?? [] : [];
				const editedValue = customFieldEdits[node.key];
				const inputValue = editedValue ?? node.value ?? '';
				const label = node.displayName;
				const displayText = node.displayValue ?? node.value ?? '—';

				let inputType = 'text';
				if (node.type === 'INTEGER' || node.type === 'DECIMAL') inputType = 'number';
				else if (node.type === 'DATE') inputType = 'date';

				const editable = canManage && !readOnly;

				return (
					<CustomFieldRow key={node.id}>
						<Detail color="medium">{label}</Detail>
						{!editable ? (
							<Text size="2" color={node.value ? undefined : 'auxiliary'}>
								{displayText}
							</Text>
						) : node.type === 'DROPDOWN' && options.length > 0 ? (
							<CustomFieldSelect
								aria-label={label}
								value={inputValue}
								disabled={saving}
								onChange={e => {
									handleCustomFieldChange(node.key, e.target.value);
									saveCustomField(node.key, e.target.value);
								}}
							>
								<option value="">— Select —</option>
								{options.map(opt => {
									// The value sent is the option id decoded from its global id
									// (`Type:value`). Join past the first segment so a value that
									// itself contains ':' (a time, a URL) is not truncated.
									const optionValue = atob(opt.id).split(':').slice(1).join(':');
									return (
										<option key={opt.id} value={optionValue}>
											{opt.displayName}
										</option>
									);
								})}
							</CustomFieldSelect>
						) : (
							<CustomFieldInput
								type={inputType}
								aria-label={label}
								value={inputValue}
								disabled={saving}
								onChange={e => handleCustomFieldChange(node.key, e.target.value)}
								onBlur={() => commitCustomField(node.key)}
							/>
						)}
					</CustomFieldRow>
				);
			})}

			<Divider />

			<Footer>
				<Provenance>
					{channelIcon && <Icon icon={channelIcon} size="s" newColor="disabled" />}
					<MetaChip
						label="Change channel"
						options={selectableChannels()}
						current={ticket.channel as TicketChannel}
						disabled={!canManage || saving}
						onSelect={channel => onUpdateField?.('channel', channel)}
						renderValue={channel => <Text size="2">{CHANNEL_LABELS[channel]}</Text>}
						data-cy="meta-channel"
					/>
				</Provenance>
				<Detail color="medium">{relativeTime(ticket.createdAt)}</Detail>
			</Footer>

			{ticket.closedAt && (
				<Pill variant={PillVariant.NEUTRAL}>{`Closed ${relativeTime(ticket.closedAt as string)}`}</Pill>
			)}
		</Panel>
	);
}
